import { useState } from 'react'
import { X, Minus, Plus, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'

interface QuoteModalProps {
  isOpen: boolean
  onClose: () => void
  planName: string
  planPrice: number
  planDescription: string
  userEmail?: string
  userName?: string
  companyId?: string
}

export function QuoteModal({
  isOpen,
  onClose,
  planName,
  planPrice,
  planDescription,
  userEmail = '',
  userName = '',
  companyId = '',
}: QuoteModalProps) {
  const [quantity, setQuantity] = useState(1)
  const [formData, setFormData] = useState({
    name: userName,
    email: userEmail,
    message: ''
  })
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState<'form' | 'success' | 'error'>('form')
  const [errorMsg, setErrorMsg] = useState('')

  const totalPrice = planPrice * quantity

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1) setQuantity(newQuantity)
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const resetForm = () => {
    setQuantity(1)
    setFormData({ name: userName, email: userEmail, message: '' })
    setView('form')
    setErrorMsg('')
  }

  const closeAndReset = () => {
    if (loading) return
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setErrorMsg('')

    try {
      // Store quote request in database
      const { error } = await supabase
        .from('quote_requests')
        .insert({
          company_id: companyId || null,
          plan_name: planName,
          plan_price: planPrice,
          quantity: quantity,
          total_price: totalPrice,
          requester_name: formData.name,
          requester_email: formData.email,
          message: formData.message,
          status: 'pending'
        })

      if (error) {
        // If table doesn't exist, fall back to email
        console.log('Quote table not available, using email fallback')
        window.location.href = `mailto:sales@helpdesk411.com?subject=Quote Request - ${planName} Plan (${quantity} computers)&body=Name: ${formData.name}%0AEmail: ${formData.email}%0APlan: ${planName}%0AQuantity: ${quantity}%0ATotal: $${totalPrice}/month%0A%0AMessage: ${formData.message}`
        closeAndReset()
        return
      }

      setView('success')
    } catch (err: any) {
      setErrorMsg(err.message || 'Something went wrong. Please try again.')
      setView('error')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={closeAndReset}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{planName}</h2>
            <p className="text-sm text-gray-500 mt-1">Request a quote</p>
          </div>
          <button
            onClick={closeAndReset}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {view === 'form' && (
            <>
              {/* Pricing */}
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  ${planPrice}/Computer
                </div>
                <p className="text-sm text-gray-600">{planDescription}</p>
              </div>

              {/* Quantity Selector */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleQuantityChange(quantity - 1)}
                    className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-white transition-colors"
                    type="button"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={e => handleQuantityChange(parseInt(e.target.value) || 1)}
                    className="w-16 text-center border border-gray-300 rounded-lg px-2 py-1 bg-white"
                    min="1"
                  />
                  <button
                    onClick={() => handleQuantityChange(quantity + 1)}
                    className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-white transition-colors"
                    type="button"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Total/month:</div>
                  <div className="text-2xl font-bold text-gray-900">${totalPrice}</div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message (optional)
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={e => handleInputChange('message', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
                    rows={3}
                    placeholder="Any specific requirements or questions?"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Request Quote'
                  )}
                </button>
              </form>
            </>
          )}

          {view === 'success' && (
            <div className="text-center space-y-4">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
              <h3 className="text-xl font-semibold text-gray-900">Quote Requested!</h3>
              <p className="text-gray-600">
                We'll review your request and get back to you at {formData.email} within 24 hours.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-left text-sm">
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Plan</span>
                  <span className="font-medium">{planName}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Computers</span>
                  <span className="font-medium">{quantity}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Monthly Total</span>
                  <span className="font-semibold">${totalPrice}</span>
                </div>
              </div>
              <button
                onClick={closeAndReset}
                className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                Done
              </button>
            </div>
          )}

          {view === 'error' && (
            <div className="text-center space-y-4">
              <AlertCircle className="w-12 h-12 text-red-600 mx-auto" />
              <h3 className="text-xl font-semibold text-gray-900">Something went wrong</h3>
              <p className="text-gray-600">{errorMsg}</p>
              <div className="flex gap-3">
                <button
                  onClick={closeAndReset}
                  className="flex-1 border border-gray-300 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => setView('form')}
                  className="flex-1 bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
